import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Users, Clock, Award, ArrowRight } from "lucide-react";

const SKYLINE = "https://images.unsplash.com/photo-1755531196237-0cb37838cb05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxoYWxpZmF4JTIwbm92YSUyMHNjb3RpYSUyMHNreWxpbmV8ZW58MHx8fHwxNzg0NDI2ODQ0fDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { n: "10k+", l: "Rides completed" },
  { n: "4.9★", l: "Average rating" },
  { n: "24/7", l: "Availability" },
  { n: "100%", l: "Upfront pricing" },
];

const values = [
  { icon: ShieldCheck, t: "Safety first", d: "Fully licensed, insured, and background-checked drivers on every trip." },
  { icon: Clock, t: "Punctual", d: "We track your flight and plan pickups so you're never left waiting." },
  { icon: Users, t: "People-first", d: "Friendly local drivers who treat every passenger like a guest." },
  { icon: Award, t: "Premium comfort", d: "Clean, well-maintained vehicles for a smooth, first-class ride." },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

export default function About() {
  return (
    <div>
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pt-20 pb-16 grid gap-14 lg:grid-cols-2 items-center">
        <motion.div {...fade(0)}>
          <span className="text-sm uppercase tracking-[0.2em] text-gold">About us</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">
            Halifax's trusted name in transfers &amp; tours.
          </h1>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed">
            Bridge Taxi, Airport Shuttle &amp; Tours was founded on a simple idea: getting around Nova Scotia
            should be effortless, comfortable, and fairly priced. From early-morning airport runs to
            wedding-day arrivals and scenic coastal tours, our local drivers deliver a dependable,
            first-class experience every single time.
          </p>
          <p className="mt-4 text-slate-400 leading-relaxed">
            We proudly serve Halifax and the surrounding communities with transparent fares you can
            see before you book — no surge pricing, no hidden fees.
          </p>
          <Link to="/book" data-testid="about-book-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-1 hover:bg-gold-light">
            Book a ride <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
        <motion.div {...fade(0.1)}>
          <img src={SKYLINE} alt="Halifax" className="rounded-sm border border-gold/20 max-h-[80vh] w-full object-cover" />
        </motion.div>
      </section>

      <section className="bg-navy-light border-y border-gold/10">
        <div className="max-w-[1720px] mx-auto px-5 sm:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div key={s.l} {...fade(i * 0.08)} className="text-center">
              <div className="font-heading text-4xl font-bold text-gold">{s.n}</div>
              <div className="mt-2 text-sm text-slate-400 uppercase tracking-wider">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 py-24">
        <motion.div {...fade(0)} className="mb-12 max-w-2xl">
          <span className="text-sm uppercase tracking-[0.2em] text-gold">Our values</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">What drives us</h2>
        </motion.div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <motion.div key={v.t} {...fade(i * 0.08)} className="rounded-sm bg-navy-light border border-gold/20 p-8 transition-transform duration-200 hover:-translate-y-1.5">
              <div className="grid place-items-center h-12 w-12 rounded-sm bg-gold/10 text-gold mb-5"><v.icon className="h-6 w-6" /></div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">{v.t}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
